/**
 * Macro Tree Types
 *
 * Persisted data model for the stateful macro system. Each campaign that opts
 * in (`HostCampaignConfig.useStatefulMacroSystem === true`) can author one
 * `MacroTreeGraph` per `MacroTrigger`:
 *
 *   - `'launch'`  — the launch macro: a walk from the start node to an end node.
 *   - `'watcher'` — the scheduled-watcher reference: a single node whose
 *                   expected state is checked periodically while idle.
 *
 * Recognition is `playcast-visual256-v2` only. Trees carry reference images
 * (`sourceUrl`) and regions; the calibrated descriptors live in the analysis
 * artifact attached at lock time, never in the tree.
 *
 * Locked-tree invariants live in `macroTreeRules.ts`.
 */

import { z } from 'zod';
import { refineAuthoringMacroTreeGraph, refineLockedMacroTreeGraph } from './macroTreeRules';
import { publishedCalibrationEvidenceSchema } from './macroCalibration/evidence';

// ============================================================================
// PROFILE + TRIGGER + FAILURE ENUMS
// ============================================================================

/** The only recognition profile. Bundles, artifacts and capabilities all name it. */
export { MACRO_VISUAL_PROFILE, type MacroVisualProfile } from './macroProfile';

/** Which campaign event a macro tree drives. */
export const macroTriggerSchema = z.enum(['launch', 'watcher']);
export type MacroTrigger = z.infer<typeof macroTriggerSchema>;

/**
 * Per-node failure response when no child variant matches.
 * `'restart'` = full game relaunch from root, retry-budgeted.
 */
export const macroOnFailureSchema = z.enum(['restart']);
export type MacroOnFailure = z.infer<typeof macroOnFailureSchema>;

// ============================================================================
// NODE KIND (definitive start / step / end markers)
// ============================================================================

/**
 * Explicit role of a node in the graph.
 *
 *  - `'start'` — the unique node the run begins from. Its id MUST equal
 *     `rootNodeId` (the redundancy is intentional — admin tools and the
 *     runtime read different fields, and `macroTreeRules` pins them together).
 *  - `'step'` — an intermediate node. Has at least one outgoing edge. The
 *     default for any newly authored node.
 *  - `'end'` — terminal node. The run completes when captures match an
 *     `'end'` child. End nodes MUST have no outgoing edges.
 *  - `'wait'` — poll gate. No reference, no macros. The runtime polls until a
 *     child reference matches or `waitTimeoutMs` elapses. Never start/end,
 *     never a leaf, never the root. Every child must have `expectedState`.
 */
export const macroNodeKindSchema = z.enum(['start', 'step', 'end', 'wait']);
export type MacroNodeKind = z.infer<typeof macroNodeKindSchema>;

/**
 * Maximum allowed value for `MacroGroup.postMacroDelayMs` (10 minutes).
 * Recorded settles may span multiple minutes (e.g. long game loads) so the cap
 * must accommodate real-world gaps. The canvas renders a wait-time badge on the
 * incoming edge (pink > 60 s, red > 300 s) to surface unusually long delays.
 */
export const MACRO_POST_MACRO_DELAY_MAX_MS = 600_000;

/** Wait-node poll interval (sleep between captures). */
export const MACRO_WAIT_INTERVAL_MIN_MS = 250;
export const MACRO_WAIT_INTERVAL_MAX_MS = 60_000;
export const MACRO_WAIT_INTERVAL_DEFAULT_MS = 1_000;

/**
 * Campaign launch base timeout (host-owned, 8 minutes). Any Wait-node poll
 * deadline must elapse strictly before this so the wait fails first and the
 * campaign switch timeout never preempts it.
 */
export const CAMPAIGN_SWITCH_BASE_TIMEOUT_MS = 480_000;

/** Safety margin between a capped wait deadline and the switch timeout. */
export const MACRO_WAIT_TIMEOUT_CAP_MARGIN_MS = 5_000;

/**
 * Hard ceiling the host applies to any wait deadline:
 * `CAMPAIGN_SWITCH_BASE_TIMEOUT_MS − MACRO_WAIT_TIMEOUT_CAP_MARGIN_MS`.
 */
export const MACRO_WAIT_TIMEOUT_CAP_MS =
  CAMPAIGN_SWITCH_BASE_TIMEOUT_MS - MACRO_WAIT_TIMEOUT_CAP_MARGIN_MS;

/**
 * Authoring maximum for wait nodes — derived from the same source as the
 * host's runtime cap so the editor can never create a wait the runtime will
 * not honor.
 */
export const MACRO_WAIT_TIMEOUT_AUTHORING_MAX_MS = MACRO_WAIT_TIMEOUT_CAP_MS;

/** Wire-schema maximum (10 min); the host still caps at `MACRO_WAIT_TIMEOUT_CAP_MS`. */
export const MACRO_WAIT_TIMEOUT_MAX_MS = 600_000;
export const MACRO_WAIT_TIMEOUT_DEFAULT_MS = 60_000;

// ============================================================================
// REGION (node-level crop in reference pixels)
// ============================================================================

export const macroRegionSchema = z.object({
  x: z.number().int().nonnegative(),
  y: z.number().int().nonnegative(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});
export type MacroRegion = z.infer<typeof macroRegionSchema>;

/**
 * Canonical key for a node's `expectedState.region`, as used by the analysis
 * manifest's `ReferenceRow.regionKey`. Absent / degenerate (non-positive
 * extent) -> `""` (full-frame). Otherwise `x,y,width,height` after truncating
 * each field to an integer.
 *
 * Throws when a field is present but not a finite number. Callers MUST handle
 * that throw and fail closed; never propagate it to a render path.
 */
export function regionKey(region?: MacroRegion | null): string {
  if (region == null) {
    return '';
  }
  if (typeof region !== 'object' || Array.isArray(region)) {
    throw new Error('region must be an object with x, y, width, and height');
  }
  const x = sanitizeRegionInt(region.x, 'x');
  const y = sanitizeRegionInt(region.y, 'y');
  const width = sanitizeRegionInt(region.width, 'width');
  const height = sanitizeRegionInt(region.height, 'height');
  if (width <= 0 || height <= 0) {
    return '';
  }
  return `${x},${y},${width},${height}`;
}

function sanitizeRegionInt(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`region.${field} must be a finite number`);
  }
  return Math.trunc(value);
}

// ============================================================================
// SNAPSHOT (one reference frame for a node's expected screen state)
// ============================================================================

/**
 * One reference screenshot for a node. A node may carry multiple snapshots
 * captured at different game-states (e.g. animation frames of a title screen);
 * a node matches when ANY of its calibrated references matches.
 *
 * `width`/`height` are extracted from the uploaded image at admin-dash time
 * and travel per-snapshot so a single node may carry frames at multiple
 * resolutions if needed.
 */
export const macroSnapshotSchema = z.object({
  /** Preserved capture-time evidence, distinct from native analysis and playback references. */
  calibrationEvidence: publishedCalibrationEvidenceSchema.optional(),
  /** Stable uuid v4. Primary key for the snapshot's S3 object. */
  id: z.string().uuid(),
  /** Capture resolution (extracted from the source image at upload time). */
  width: z.number().int().positive().max(16_384),
  height: z.number().int().positive().max(16_384),
  /**
   * Image shown in the editor; never analyzed. Admin uploads store a 480p
   * preview here; recorder-promoted snapshots reuse the native PNG until the
   * server gains an image resizer.
   */
  previewUrl: z.string().url(),
  /** Native-resolution PNG the analysis reads. */
  sourceUrl: z.string().url(),
}).strict();
export type MacroSnapshot = z.infer<typeof macroSnapshotSchema>;

/** Expected screen state: reference snapshots plus an optional crop. */
export const macroExpectedStateSchema = z.object({
  /** At least one reference snapshot. */
  snapshots: z.array(macroSnapshotSchema).min(1),
  /** Node-level crop in reference pixels; applied to every snapshot. */
  region: macroRegionSchema.optional(),
  /** Maximum rounded raw fused difference accepted as a match (0 = exact). */
  matchThresholdPercent: z.number().int().min(0).max(100).optional(),
}).strict();
export type MacroExpectedState = z.infer<typeof macroExpectedStateSchema>;

// ============================================================================
// MACRO GROUP (a node in the tree — one game-state plus its transition macros)
// ============================================================================

/**
 * A single node in the macro tree. Carries:
 *   - the macros to run when entering this node (existing key=/string=/gpad= syntax),
 *   - per-node failure behavior (`onFailure`),
 *   - and (optionally) the expected screen state used by its parent to identify
 *     us as the matched child.
 *
 * The root/start node never needs `expectedState` on a `launch` tree (it is
 * selected by kind). Non-root nodes may omit `expectedState` when the node is
 * the SOLE child of its parent (reference-optional linear advance). When a
 * parent has MULTIPLE children (a branch), every child MUST have
 * `expectedState`. `macroTreeRules` enforces this for locked trees; the draft
 * schema is permissive.
 */
export const macroGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(80),
  position: z.object({ x: z.number(), y: z.number() }),
  /** Macro steps in DSL grammar:
   *
   *   key=<keyCode>@<delayMs>^<holdMs>
   *   string=<text>@<delayMs>
   *   gpad=<BTN>@<delayMs>^<holdMs>
   *   mouse=move^<dx>,<dy>@<delayMs>          # pointer-lock relative move (pixel deltas)
   *   mouse=down^<button>@<delayMs>           # button ∈ 0|1|2 (left|right|middle)
   *   mouse=up^<button>@<delayMs>
   *   mouse=wheel^<deltaY>[,<deltaX>]@<delayMs> # Windows wheel units (120/detent), +Y up / +X right
   *   mouse=abs^<x>,<y>@<delayMs>             # x,y ∈ [0,1] preview-normalized; delay on abs
   *   touch=down@<delayMs>^<x>,<y>[,<touchId>] # x,y ∈ [0,1], ID 0–255; omitted ID means 0
   *   touch=move@<delayMs>^<x>,<y>[,<touchId>]
   *   touch=up@<delayMs>^<x>,<y>[,<touchId>]   # up to ten simultaneous independent contacts
   */
  macros: z.array(z.string()),
  /** Additive independent timing; sourceMacros invalidates it after legacy editor edits. */
  actionTimeline: z.object({
    version: z.literal(1),
    sourceMacros: z.array(z.string()),
    events: z.array(z.object({
      atMs: z.number().int().min(0).max(600_000),
      macros: z.array(z.string()),
    })).max(10_000),
  }).optional(),
  /** What the host should do when no child variant matches. */
  onFailure: macroOnFailureSchema,
  /**
   * Definitive role in the graph. See `macroNodeKindSchema` for the full
   * contract. New nodes default to `'step'`; the unique `'start'` node is
   * also referenced by `MacroTreeGraph.rootNodeId`.
   */
  kind: macroNodeKindSchema,
  /**
   * Milliseconds to wait after running this node's `macros` before capturing
   * for the next match. Lets the admin pad in a short settle delay for
   * animations / fade-ins that would otherwise be captured mid-transition.
   * 0 = no delay (the default).
   *
   * Derived from recorded timestamps: the gap between the last input end of
   * node N and the timestamp of the next screen-state mark. May exceed 60 s
   * for multi-minute game loads. The canvas renders a wait-time badge on the
   * incoming edge (pink > 60 s, red > 300 s).
   */
  postMacroDelayMs: z.number().int().min(0).max(MACRO_POST_MACRO_DELAY_MAX_MS).default(0),
  /**
   * Poll interval while waiting for a reference match. Required on locked
   * trees when `kind === 'wait'` (child screens) or `waitEnabled === true`
   * (this node's `expectedState`). Omit otherwise. Drafts may omit.
   */
  waitIntervalMs: z
    .number()
    .int()
    .min(MACRO_WAIT_INTERVAL_MIN_MS)
    .max(MACRO_WAIT_INTERVAL_MAX_MS)
    .optional(),
  /**
   * Wall-clock budget to match a reference before restart. Required with
   * `waitIntervalMs` on locked Wait nodes and on `waitEnabled` nodes; on a
   * `watcher` tree it is the check deadline (1 s .. 30 s).
   * Must be >= `waitIntervalMs` and <= MACRO_WAIT_TIMEOUT_MAX_MS when present.
   */
  waitTimeoutMs: z
    .number()
    .int()
    .min(MACRO_WAIT_INTERVAL_MIN_MS)
    .max(MACRO_WAIT_TIMEOUT_MAX_MS)
    .optional(),
  /**
   * When true, the runtime polls this node's `expectedState` until it matches
   * (or timeout) before running this node's macros. Omit or false = one-shot
   * match. Forbidden on `kind: 'wait'` (S2).
   */
  waitEnabled: z.boolean().optional(),
  expectedState: macroExpectedStateSchema.optional(),
});
export type MacroGroup = z.infer<typeof macroGroupSchema>;

// ============================================================================
// EDGE (parent → child relationship)
// ============================================================================

export const macroTreeEdgeSchema = z.object({
  id: z.string().uuid(),
  source: z.string().uuid(),
  target: z.string().uuid(),
});
export type MacroTreeEdge = z.infer<typeof macroTreeEdgeSchema>;

// ============================================================================
// FORK (UI-only sidecar — does NOT affect the runtime)
// ============================================================================

/**
 * A fork point in the editor canvas. Forks are PURELY a visual cleanup
 * affordance: they do NOT exist in the runtime and are NOT referenced by
 * `MacroTreeGraph.edges`. The canvas expands every fork into the cartesian
 * product of (ultimate-parents × ultimate-children) direct group→group edges
 * at save time, and the expanded edges are what the runtime follows.
 *
 * The editor stores forks in `metadata.forks` solely so the same clean
 * topology can be re-rendered when the admin reopens the tree. `inputs`
 * and `outputs` may reference either group ids OR other fork ids (chained
 * fan-out/fan-in is allowed in the editor).
 *
 * Runtime consumers MUST NOT read this field — it is exclusively for the
 * admin-dashboard editor.
 */
export const macroForkSchema = z.object({
  id: z.string().uuid(),
  position: z.object({ x: z.number(), y: z.number() }),
  inputs: z.array(z.string().uuid()),
  outputs: z.array(z.string().uuid()),
});
export type MacroFork = z.infer<typeof macroForkSchema>;

// ============================================================================
// TREE GRAPH (entire macro tree for one (campaign, trigger) pair)
// ============================================================================
//
// Two flavors live here:
//
//   • macroTreeGraphSchema       — STRICT (used by the LOCKED slot). The
//                                   lock gate and every runtime consumer go
//                                   through this; `macroTreeRules` enforces
//                                   run-readiness invariants.
//   • macroTreeGraphDraftSchema  — PERMISSIVE (used by the DRAFT slot).
//                                   Authors can persist any in-progress
//                                   topology — empty trees, missing root,
//                                   orphan edges, no 'start' node, etc.
//                                   The runtime NEVER reads from the draft
//                                   slot, so structural-only checks are
//                                   sufficient.
//

const macroTreeGraphBaseSchema = z.object({
  /** Which trigger this tree drives. */
  trigger: macroTriggerSchema,
  /** Run starts here. Must reference a node in `nodes` for the strict schema; may be empty in drafts. */
  rootNodeId: z.string().uuid(),
  nodes: z.array(macroGroupSchema).min(1),
  edges: z.array(macroTreeEdgeSchema),
  metadata: z.object({
    version: z.string(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
    /** UI-only sidecar (see `macroForkSchema`). */
    forks: z.array(macroForkSchema).optional(),
  }),
});
/** Structurally valid graph before locked-tree rules run. Input type for `macroTreeRules`. */
export type MacroTreeGraphInput = z.infer<typeof macroTreeGraphBaseSchema>;

/**
 * Permissive base for DRAFT trees. Differs from the strict base only in:
 *  - `rootNodeId` may be the empty string (no root selected yet).
 *  - `nodes` may be empty (clean editor canvas).
 *
 * Everything else still has to be type-correct (Zod still rejects
 * malformed JSON, wrong field types, etc.) so a draft round-trips cleanly
 * and renders without runtime crashes.
 */
const macroTreeGraphDraftBaseSchema = macroTreeGraphBaseSchema.extend({
  rootNodeId: z.string(),
  nodes: z.array(macroGroupSchema),
});

export const macroTreeGraphSchema = macroTreeGraphBaseSchema.superRefine(refineLockedMacroTreeGraph);
export type MacroTreeGraph = z.infer<typeof macroTreeGraphSchema>;

/**
 * Permissive schema for the DRAFT slot. NO refinements — authors can save
 * a half-finished graph (empty, missing root, no end node, leaves with
 * kind: 'step', orphan edge refs, cycles) and reload it intact. The Lock
 * action re-validates before promoting a draft to the locked slot.
 *
 * Runtime consumers MUST NOT read from drafts.
 */
export const macroTreeGraphDraftSchema = macroTreeGraphDraftBaseSchema;
export type MacroTreeGraphDraft = z.infer<typeof macroTreeGraphDraftSchema>;

/**
 * Authoring validation for a graph that is submitted for analysis or lock.
 * Accepts exactly the empty representation (a deliberate disabled revision)
 * or a non-empty graph whose identifiers are unique and whose root and edge
 * references resolve globally. Disconnected components ARE allowed here —
 * they are never analyzed or executed, and Lock rejects them separately with
 * `disconnected_components` once the projection cannot cover every node.
 *
 * Runtime consumers MUST NOT execute this shape directly; only
 * `macroExecutionGraphSchema` may cross the analysis/execution boundary.
 */
export const macroTreeGraphAuthoringSchema = macroTreeGraphDraftBaseSchema.superRefine(refineAuthoringMacroTreeGraph);
export type MacroTreeGraphAuthoring = z.infer<typeof macroTreeGraphAuthoringSchema>;

/**
 * The one exact empty representation: no nodes, no edges, and an empty root.
 * Partially empty combinations (nodes without a resolved root, or edges
 * without nodes) fail `macroTreeGraphAuthoringSchema`.
 *
 * The type is inferred rather than annotated with `z.ZodType<...>` because
 * Zod's input type marks defaulted node fields optional, which makes the
 * explicit annotation unassignable while changing no runtime behavior.
 */
export const emptyMacroTreeGraphSchema = macroTreeGraphBaseSchema.extend({
  rootNodeId: z.literal(''),
  nodes: z.tuple([]),
  edges: z.tuple([]),
});
/**
 * The canonical empty authoring shape: `MacroTreeGraph` with no content.
 * Stated structurally so downstream phases can name it without re-deriving
 * the schema's inference.
 */
export type EmptyMacroTreeGraph = MacroTreeGraph & { rootNodeId: ''; nodes: []; edges: [] };

/**
 * The locked slot accepts exactly two shapes: the exact empty representation
 * (a deliberately disabled launch revision) or the strict execution graph.
 * Partially disconnected authoring graphs are never valid here — Lock refuses
 * them with `disconnected_components`.
 */
export const lockedMacroTreeGraphSchema = z.union([emptyMacroTreeGraphSchema, macroTreeGraphSchema]);
export type LockedMacroTreeGraph = z.infer<typeof lockedMacroTreeGraphSchema>;
